/**
 * @fileoverview State and actions for the B2B event create/edit form.
 * @module pages/b2bEventForm/useB2BEventForm
 */

import { use, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import dayjs from "dayjs";
import { AppContext } from "../../context/AppContext";
import {
  getAllB2BEvents,
  initializeB2BEventCreation,
  showEventCreatedSuccess,
  submitB2BEvent,
} from "../../logic/b2bEvents";
import { canManageB2BEventStaffActions } from "../../utils/accessControl";
import { validateBusinessEventName } from "../../utils/common.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import useRequireAccess from "../../hooks/useRequireAccess";
import { useTranslation } from "react-i18next";
import styles from "../../styles/B2BEventForm.module.css";
import {
  BANNER_MAX_BYTES,
  BANNER_MIME_TYPES,
  DATE_FORMAT,
  isB2BEventSaveDisabled,
  nextB2BAudience,
  participatingSearchBarWidth,
  readFormViewportFlags,
} from "./b2bEventFormModel";
import { useB2BEventParticipants } from "./useB2BEventParticipants";

const INITIAL_FORM_DATA = {
  imagenBanner: null,
  audiencia: null,
  nombre: "",
  fechaInicioEvento: null,
  fechaFinEvento: null,
  descripcion: "",
  fechaDisponibleSocios: null,
  fechaDisponibleNoSocios: null,
  fechaVigenciaCompradores: null,
  fechaLimiteRegistro: null,
  horaInicio: null,
  horaFin: null,
  costoSocios: "",
  costoNoSocios: "",
  duracionCitasSocios: null,
  duracionCitasNoSocios: null,
  ubicacion: null,
  activo: true,
};

/**
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
const formatEventDate = (value) =>
  value ? dayjs(value).format(DATE_FORMAT) : null;

/**
 * Load events and the form once access and the route state are known.
 * @param {Object} input
 * @returns {Promise<void>}
 */
const loadB2BEventForm = async ({
  location,
  navigate,
  changePermissions,
  setAllB2BEvents,
  setFormData,
  setParticipatingCompanies,
  setLoading,
}) => {
  const token = localStorage.getItem("token");
  if (!token || token.trim() === "undefined") {
    return;
  }
  const userId = localStorage.getItem("userId");
  const allEvents = await getAllB2BEvents(navigate);
  setAllB2BEvents(allEvents);
  await initializeB2BEventCreation({
    eventId: location.state?.eventData?._id,
    userId,
    changePermissions,
    navigate,
    dayjs,
    setFormData,
    setParticipatingCompanies,
    setIsLoading: setLoading,
  });
};

/**
 * Form fields, banner, save, and the create-success toast.
 * The list of events still loads only after access is allowed.
 * @returns {Object}
 */
const useB2BEventFormData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageB2BEventStaffActions);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [participatingCompanies, setParticipatingCompanies] = useState([]);
  const [showError, setShowError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [allB2BEvents, setAllB2BEvents] = useState([]);
  const [nameValidation, setNameValidation] = useState({ isValid: true });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    if (location.state?.isEditMode && location.state?.eventData) {
      setIsEditMode(true);
      setEventData(location.state.eventData);
    } else {
      setIsEditMode(false);
      setEventData(null);
    }

    loadB2BEventForm({
      location,
      navigate,
      changePermissions,
      setAllB2BEvents,
      setFormData,
      setParticipatingCompanies,
      setLoading,
    });
    // Load when access or the route state changes, matching the previous effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, location.state, navigate, location.pathname]);

  useEffect(() => {
    if (allB2BEvents.length > 0 && formData.nombre) {
      setNameValidation(
        validateBusinessEventName(formData.nombre, allB2BEvents, eventData?._id),
      );
    }
  }, [allB2BEvents, formData.nombre, eventData?._id]);

  return {
    navigate,
    allowed,
    formData,
    setFormData,
    showError,
    setShowError,
    hasChanges,
    setHasChanges,
    saving,
    setSaving,
    loading,
    error,
    setError,
    imageChanged,
    setImageChanged,
    imageFormat,
    setImageFormat,
    imageName,
    setImageName,
    isEditMode,
    setIsEditMode,
    eventData,
    setEventData,
    allB2BEvents,
    setAllB2BEvents,
    nameValidation,
    setNameValidation,
    showSuccessMessage,
    setShowSuccessMessage,
    isSlidingUp,
    setIsSlidingUp,
    imageRef,
    participatingCompanies,
    setParticipatingCompanies,
  };
};

/**
 * Banner file checks and drag highlight. The dropped file still arrives through the input.
 * @param {Object} form
 * @param {Function} t
 * @returns {Object}
 */
const useB2BEventBanner = (form, t) => {
  const clearRejectedBanner = useCallback(() => {
    form.setImageChanged(false);
    form.setFormData((prev) => ({ ...prev, imagenBanner: null }));
    form.setImageName(null);
    form.setImageFormat(null);
  }, [form]);

  const handleImageChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      if (!BANNER_MIME_TYPES.includes(file.type)) {
        form.setError(t("pages.buyerCompanyForm.logoFileTypeError"));
        clearRejectedBanner();
        return;
      }
      if (file.size > BANNER_MAX_BYTES) {
        form.setError(t("pages.buyerCompanyForm.logoFileSizeError"));
        form.setImageChanged(false);
        form.setFormData((prev) => ({ ...prev, imagenBanner: null }));
        form.setImageFormat(null);
        form.setImageName(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        form.setImageChanged(true);
        form.setFormData((prev) => ({
          ...prev,
          imagenBanner: loadEvent.target.result,
        }));
        form.setImageName(file.name);
        form.setImageFormat(file.type);
        form.setHasChanges(true);
      };
      reader.readAsDataURL(file);
    },
    [clearRejectedBanner, form, t],
  );

  const onDragEnter = useCallback(() => {
    form.imageRef.current?.classList.add(styles.dragOver);
  }, [form.imageRef]);
  const onDragLeave = useCallback(() => {
    form.imageRef.current?.classList.remove(styles.dragOver);
  }, [form.imageRef]);
  const onDrop = useCallback(() => {
    form.imageRef.current?.classList.remove(styles.dragOver);
  }, [form.imageRef]);

  return { handleImageChange, onDragEnter, onDragLeave, onDrop };
};

/**
 * Build the create/update payload and keep the success toast on a new event.
 * @param {Object} event
 * @param {Object} form
 * @returns {Promise<void>}
 */
const submitCurrentEvent = async (event, form) => {
  event.preventDefault();
  form.setSaving(true);
  form.setShowError(false);
  try {
    const eventToSubmit = {
      ...form.formData,
      nombreImagen: form.imageChanged ? form.imageName : null,
      formatoImagen: form.imageChanged ? form.imageFormat : null,
      userId: localStorage.getItem("userId"),
      fechaInicioEvento: dayjs(form.formData.fechaInicioEvento).format(DATE_FORMAT),
      fechaFinEvento: dayjs(form.formData.fechaFinEvento).format(DATE_FORMAT),
      fechaDisponibleSocios: formatEventDate(form.formData.fechaDisponibleSocios),
      fechaDisponibleNoSocios: formatEventDate(form.formData.fechaDisponibleNoSocios),
      fechaVigenciaCompradores: formatEventDate(form.formData.fechaVigenciaCompradores),
      fechaLimiteRegistro: formatEventDate(form.formData.fechaLimiteRegistro),
    };
    const result = await submitB2BEvent(
      eventToSubmit,
      form.eventData?._id,
      form.navigate,
      form.setSaving,
      form.setEventData,
      form.setHasChanges,
    );
    if (result?.ok && result.wasCreate) {
      form.setIsEditMode(true);
      form.setImageChanged(false);
      if (result.event) {
        form.setAllB2BEvents((prev) => [...prev, result.event]);
      }
      showEventCreatedSuccess(form.setShowSuccessMessage, form.setIsSlidingUp);
    }
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    form.setSaving(false);
    form.setShowError(true);
  }
};

/**
 * Save, field edits, and the audience checkboxes.
 * @param {Object} form
 * @returns {Object}
 */
const useB2BEventFormActions = (form) => {
  const handleSubmit = useCallback((event) => submitCurrentEvent(event, form), [form]);

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      form.setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === "nombre" && form.allB2BEvents.length > 0) {
        form.setNameValidation(
          validateBusinessEventName(value, form.allB2BEvents, form.eventData?._id),
        );
      }
      form.setHasChanges(true);
    },
    [form],
  );

  const handleAudienciaCheckboxChange = useCallback(
    (which) => {
      form.setFormData((prev) => ({
        ...prev,
        audiencia: nextB2BAudience(prev.audiencia, which),
      }));
      form.setHasChanges(true);
    },
    [form],
  );

  const handleActivoChange = useCallback(
    (event) => {
      form.setHasChanges(true);
      form.setFormData((prev) => ({ ...prev, activo: event.target.checked }));
    },
    [form],
  );

  const handleGoBack = useCallback(() => {
    form.navigate(-1);
  }, [form]);

  const saveDisabled = isB2BEventSaveDisabled({
    hasChanges: form.hasChanges,
    saving: form.saving,
    loading: form.loading,
    formData: form.formData,
    nameValidation: form.nameValidation,
  });

  return {
    handleSubmit,
    handleInputChange,
    handleAudienciaCheckboxChange,
    handleActivoChange,
    handleGoBack,
    saveDisabled,
  };
};

/**
 * B2B event create/edit form: details, banner, companies, and save.
 * @returns {Object}
 */
export function useB2BEventForm() {
  const { t } = useTranslation();
  const form = useB2BEventFormData();
  const banner = useB2BEventBanner(form, t);
  const actions = useB2BEventFormActions(form);
  const participants = useB2BEventParticipants(form);
  const viewport = readFormViewportFlags(window.innerWidth);

  return {
    t,
    ...form,
    ...banner,
    ...actions,
    ...participants,
    participatingCompaniesSearchBarWidth: participatingSearchBarWidth(viewport),
    eventDateStart: formatEventDate(form.formData.fechaInicioEvento),
    eventDateEnd: formatEventDate(form.formData.fechaFinEvento),
  };
}
